import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDateTime, formatCurrency } from "@/lib/utils-date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Building2, Mail, Phone, MapPin, Trash2, Pencil, Package, Sparkles, CheckCircle2, MessageCircle, GitMerge } from "lucide-react";
import ReorderSection from "@/components/ReorderSection";
import { usePerm } from "@/context/AuthContext";

export default function Suppliers() {
  const canWrite = usePerm("suppliers_write");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Passo 2 fornitori — dialog di unione. `mergeWinner` è la scheda su cui
  // l'operatore ha cliccato "Unisci con…"; il dialog mostra le altre schede
  // dello store, con le candidate al top (stesso nome normalizzato).
  const [mergeWinner, setMergeWinner] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await api.listSuppliers();
    setSuppliers(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await api.updateSupplier(editing.id, payload);
        toast.success("Fornitore aggiornato");
      } else {
        await api.createSupplier(payload);
        toast.success("Fornitore creato");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteSupplier(deleteTarget.id);
      toast.success("Fornitore eliminato");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  return (
    <div data-testid="suppliers-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">
            Rubrica
          </p>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Fornitori
          </h1>
          <p className="text-[#5C5F5A] mt-2">
            {suppliers.length} {suppliers.length === 1 ? "fornitore" : "fornitori"} ·{" "}
            {suppliers.reduce((a, s) => a + (s.items?.length || 0), 0)} articoli imparati dalle scansioni DDT
          </p>
        </div>
        {canWrite && (
        <Button
          data-testid="add-supplier-btn"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
        >
          <Plus size={16} className="mr-2" />
          Nuovo fornitore
        </Button>
        )}
      </div>

      <div className="flex items-start gap-3 p-4 rounded-lg bg-[#E8F3EC] border border-[#C5E1D1]">
        <Sparkles size={16} className="text-[#2B5A3F] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[#2B5A3F]">
          <strong>Apprendimento automatico attivo:</strong> ogni volta che scansioni un DDT o
          fattura l&apos;app salva il fornitore e memorizza i codici articolo ricorrenti. Al prossimo
          documento dello stesso fornitore ti propone subito i prodotti già visti.
        </div>
      </div>

      <ReorderSection />

      {loading ? (
        <div className="text-center py-16 text-[#8E918C]">Caricamento...</div>
      ) : suppliers.length === 0 ? (
        <div className="mag-card p-16 text-center">
          <Building2 size={40} className="mx-auto text-[#8E918C] mb-4" />
          <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Nessun fornitore ancora
          </h3>
          <p className="text-sm text-[#8E918C] mb-6">
            Scansiona un DDT o una fattura dalla pagina Prodotti per aggiungere automaticamente il
            primo fornitore, oppure inseriscilo manualmente.
          </p>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
          >
            <Plus size={16} className="mr-2" />
            Aggiungi fornitore
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => (
            <div
              key={s.id}
              data-testid={`supplier-card-${s.id}`}
              className="mag-card mag-card-hover p-5 flex flex-col gap-3 cursor-pointer"
              onClick={() => setDetail(s)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-xl bg-[#E5E1D8] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
                    <Building2 size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{s.name}</div>
                    {s.vat_number && (
                      <div className="text-xs text-[#8E918C] font-mono truncate">P.IVA {s.vat_number}</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMergeWinner(s);
                    }}
                    className="h-7 w-7 hover:bg-[#F4F1ED]"
                    data-testid={`merge-supplier-${s.id}`}
                    title="Unisci con altre schede"
                  >
                    <GitMerge size={12} />
                  </Button>
                  )}
                  {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(s);
                      setOpen(true);
                    }}
                    className="h-7 w-7 hover:bg-[#F4F1ED]"
                    data-testid={`edit-supplier-${s.id}`}
                  >
                    <Pencil size={12} />
                  </Button>
                  )}
                  {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(s);
                    }}
                    className="h-7 w-7 text-[#C65D47] hover:bg-[#FCEAE8]"
                    data-testid={`delete-supplier-${s.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                  )}
                </div>
              </div>

              <div className="space-y-1 text-xs text-[#5C5F5A]">
                {s.address && (
                  <div className="flex items-center gap-1.5 truncate">
                    <MapPin size={11} />
                    <span className="truncate">{s.address}</span>
                  </div>
                )}
                {s.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={11} />
                    <span>{s.phone}</span>
                  </div>
                )}
                {s.whatsapp && (
                  <div className="flex items-center gap-1.5 text-[#2B5A3F]">
                    <MessageCircle size={11} />
                    <span className="font-mono">+{s.whatsapp}</span>
                  </div>
                )}
                {s.email && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Mail size={11} />
                    <span className="truncate">{s.email}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 pt-2 border-t border-[#EAE7E1] text-xs">
                <div className="flex items-center gap-1 text-[#2B5A3F]">
                  <Package size={11} />
                  <span className="font-semibold">{s.product_count || 0}</span>
                  <span className="text-[#8E918C]">articoli</span>
                </div>
                <div className="text-[#8E918C]">·</div>
                <div className="text-[#8E918C]">
                  {s.document_count || 0} {s.document_count === 1 ? "scansione" : "scansioni"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SupplierSheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
        supplier={editing}
        onSave={handleSave}
      />

      <SupplierDetailSheet supplier={detail} onOpenChange={(v) => !v && setDetail(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il fornitore?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleteTarget?.name}&quot; e tutti gli articoli imparati verranno rimossi. I prodotti già
              in magazzino non saranno toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-supplier-confirm"
              className="bg-[#C65D47] hover:bg-[#A64B38]"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SupplierMergeDialog
        winner={mergeWinner}
        otherSuppliers={suppliers.filter((x) => x.id !== mergeWinner?.id)}
        onClose={() => setMergeWinner(null)}
        onMerged={() => {
          setMergeWinner(null);
          load();
        }}
      />
    </div>
  );
}

// Normalizzatore lato client — riflette _normalize_supplier_name del backend.
// Serve solo per marcare in cima le "candidate vere" (stesse parole distintive
// del winner). Il vero match è comunque server-side; qui è UX per l'operatore.
const CLIENT_GENERIC_WORDS = new Set([
  "agrisalumeria", "salumeria", "salumificio", "azienda", "agricola",
  "societa", "società", "cooperativa", "alimentari", "prodotti",
  "macelleria", "caseificio", "pastificio", "panificio", "gastronomia",
  "spaccio", "centro", "fresco", "freschi",
  "snc", "srl", "sas", "spa", "sc", "ss", "sapa", "scarl", "srls",
  "s", "n", "l", "a", "p", "c",
  "di", "de", "del", "della", "dei", "delle", "e", "and", "the", "il", "la",
]);
const CLIENT_ADDRESS_RE = /\b(via|viale|corso|piazza|piazzale|strada|str|v\.le|c\.so|p\.za|p\.zza|vicolo|largo|loc|localita|località|contrada|zona|cap|cf|c\.f|p\.iva|piva|iva|tel|fax|email|mail|www)\b/i;

function normalizeSupplierNameClient(name) {
  if (!name) return "";
  let s = String(name).normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const m = s.match(CLIENT_ADDRESS_RE);
  if (m) s = s.slice(0, m.index);
  s = s.replace(/[^\w\s]/g, " ").replace(/\d+/g, " ");
  const tokens = s.split(/\s+/).filter((t) => t && !CLIENT_GENERIC_WORDS.has(t));
  return [...new Set(tokens)].sort().join(" ");
}

function SupplierMergeDialog({ winner, otherSuppliers, onClose, onMerged }) {
  const [selected, setSelected] = useState(new Set());
  const [phase, setPhase] = useState("pick"); // "pick" | "confirm" | "busy"

  useEffect(() => {
    setSelected(new Set());
    setPhase("pick");
  }, [winner?.id]);

  if (!winner) return null;

  const winnerNorm = normalizeSupplierNameClient(winner.name);
  const enriched = otherSuppliers
    .map((s) => ({
      ...s,
      _isCandidate: normalizeSupplierNameClient(s.name) === winnerNorm && winnerNorm !== "",
    }))
    .sort((a, b) => {
      if (a._isCandidate !== b._isCandidate) return a._isCandidate ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalProductsToLink = enriched
    .filter((s) => selected.has(s.id))
    .reduce((a, s) => a + (s.product_count || 0), 0);

  const runMerge = async () => {
    setPhase("busy");
    try {
      const res = await api.mergeSuppliers(winner.id, [...selected]);
      toast.success("Schede unite", {
        description: `${res.merged_suppliers} schede eliminate, ${res.products_reassigned} prodotti riassegnati` +
          (res.products_recovered > 0 ? `, ${res.products_recovered} recuperati dagli scollegati` : ""),
      });
      onMerged();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
      setPhase("pick");
    }
  };

  return (
    <AlertDialog open={!!winner} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent
        data-testid="merge-supplier-dialog"
        className="max-w-lg max-h-[85vh] overflow-y-auto"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Unisci con &laquo;{winner.name}&raquo;</AlertDialogTitle>
          <AlertDialogDescription>
            {phase === "pick" ? (
              <>Seleziona una o più schede da fondere in <b>{winner.name}</b>. Le schede
              con lo stesso nome distintivo sono in cima e contrassegnate come candidate.</>
            ) : (
              <>Stai per eliminare <b>{selected.size}</b> {selected.size === 1 ? "scheda" : "schede"} e riassegnare
              i prodotti collegati a <b>{winner.name}</b>. L&apos;operazione non è reversibile.</>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {phase === "pick" && (
          <div className="my-3 space-y-1 max-h-[45vh] overflow-y-auto pr-1">
            {enriched.length === 0 ? (
              <div className="text-sm text-[#8E918C] italic py-4 text-center">
                Nessuna altra scheda in questo store.
              </div>
            ) : (
              enriched.map((s) => (
                <label
                  key={s.id}
                  data-testid={`merge-supplier-option-${s.id}`}
                  className={`flex items-start gap-2 p-2 rounded-md border text-sm cursor-pointer transition-all ${
                    selected.has(s.id)
                      ? "bg-[#E8F3EC] border-[#2E4A3B]"
                      : "bg-white border-[#EAE7E1] hover:border-[#2E4A3B]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium truncate">{s.name}</span>
                      {s._isCandidate && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FDF6E3] text-[#8B6518] border border-[#F2DEB0]">
                          Candidata
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#8E918C]">
                      {s.product_count || 0} articoli · {s.document_count || 0} scansioni
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        )}

        {phase === "confirm" && (
          <div className="my-3 p-3 rounded-md bg-[#FDF6E3] border border-[#F2DEB0] text-sm text-[#5C5F5A] space-y-1">
            <div>
              <b>{selected.size}</b> {selected.size === 1 ? "scheda sparirà" : "schede spariranno"}, il nome che
              resta è <b>{winner.name}</b>.
            </div>
            <div>
              Circa <b>{totalProductsToLink}</b> prodotti già collegati verranno riassegnati.
              Anche eventuali prodotti scollegati con la stessa ragione sociale saranno recuperati.
            </div>
            <div className="text-[#8B2E1A] font-semibold mt-1">
              L&apos;operazione non è reversibile.
            </div>
          </div>
        )}

        <AlertDialogFooter>
          {phase === "pick" ? (
            <>
              <AlertDialogCancel data-testid="merge-cancel">Annulla</AlertDialogCancel>
              <Button
                type="button"
                disabled={selected.size === 0}
                onClick={() => setPhase("confirm")}
                data-testid="merge-next"
                className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
              >
                Continua
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPhase("pick")}
                disabled={phase === "busy"}
                data-testid="merge-back"
              >
                Torna indietro
              </Button>
              <Button
                type="button"
                onClick={runMerge}
                disabled={phase === "busy"}
                data-testid="merge-confirm"
                className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
              >
                {phase === "busy" ? "Unione…" : "Unisci definitivamente"}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SupplierSheet({ open, onOpenChange, supplier, onSave }) {
  const [form, setForm] = useState({
    name: "",
    vat_number: "",
    address: "",
    phone: "",
    whatsapp: "",
    email: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (supplier) {
      setForm({
        name: supplier.name || "",
        vat_number: supplier.vat_number || "",
        address: supplier.address || "",
        phone: supplier.phone || "",
        whatsapp: supplier.whatsapp || "",
        email: supplier.email || "",
        notes: supplier.notes || "",
      });
    } else {
      setForm({ name: "", vat_number: "", address: "", phone: "", whatsapp: "", email: "", notes: "" });
    }
  }, [supplier, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setSaving(true);
    // Sanitize whatsapp: keep digits only (international format, no + no spaces)
    const payload = { ...form, whatsapp: form.whatsapp.replace(/\D/g, "") || null };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto overflow-x-hidden bg-white">
        <SheetHeader>
          <SheetTitle style={{ fontFamily: "Work Sans, sans-serif" }}>
            {supplier ? "Modifica fornitore" : "Nuovo fornitore"}
          </SheetTitle>
          <SheetDescription>Solo il nome è obbligatorio.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-6 w-full max-w-full">
          <Field label="Ragione sociale *">
            <Input
              data-testid="supplier-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Es: Agrisalumeria Luiset"
              required
            />
          </Field>
          <Field label="Partita IVA">
            <Input
              value={form.vat_number}
              onChange={(e) => update("vat_number", e.target.value)}
              placeholder="12345678901"
              className="font-mono"
            />
          </Field>
          <Field label="Indirizzo">
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefono">
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
            </Field>
          </div>
          <Field label="Numero WhatsApp">
            <Input
              data-testid="supplier-whatsapp-input"
              value={form.whatsapp}
              onChange={(e) => update("whatsapp", e.target.value)}
              placeholder="es: 39333 1234567"
              inputMode="tel"
            />
            <p className="text-[11px] text-[#8E918C] mt-1">
              Formato internazionale, senza &quot;+&quot; né spazi (es. 39 per l&apos;Italia → &quot;393331234567&quot;). Usato per inviare gli ordini di riordino.
            </p>
          </Field>
          <Field label="Note">
            <Textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </Field>
          <SheetFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#EAE7E1]">
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.name}
              data-testid="supplier-save-btn"
              className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
            >
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function SupplierDetailSheet({ supplier, onOpenChange }) {
  if (!supplier) return null;
  const items = supplier.items || [];
  return (
    <Sheet open={!!supplier} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto overflow-x-hidden bg-white">
        <SheetHeader>
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E5E1D8] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
              <Building2 size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle style={{ fontFamily: "Work Sans, sans-serif" }} className="truncate">
                {supplier.name}
              </SheetTitle>
              <SheetDescription>
                {supplier.document_count || 0} scansioni · {items.length} articoli imparati
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {(supplier.vat_number || supplier.address || supplier.phone || supplier.whatsapp || supplier.email) && (
            <div className="mag-card p-4 space-y-2 text-sm">
              {supplier.vat_number && (
                <div className="flex gap-2">
                  <span className="text-[#8E918C] text-xs w-20 flex-shrink-0">P.IVA</span>
                  <span className="font-mono">{supplier.vat_number}</span>
                </div>
              )}
              {supplier.address && (
                <div className="flex gap-2">
                  <span className="text-[#8E918C] text-xs w-20 flex-shrink-0">Indirizzo</span>
                  <span>{supplier.address}</span>
                </div>
              )}
              {supplier.phone && (
                <div className="flex gap-2">
                  <span className="text-[#8E918C] text-xs w-20 flex-shrink-0">Telefono</span>
                  <span>{supplier.phone}</span>
                </div>
              )}
              {supplier.whatsapp && (
                <div className="flex gap-2">
                  <span className="text-[#8E918C] text-xs w-20 flex-shrink-0">WhatsApp</span>
                  <span className="font-mono text-[#2B5A3F]">+{supplier.whatsapp}</span>
                </div>
              )}
              {supplier.email && (
                <div className="flex gap-2">
                  <span className="text-[#8E918C] text-xs w-20 flex-shrink-0">Email</span>
                  <span>{supplier.email}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Articoli imparati ({items.length})
            </h3>
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#8E918C] bg-[#F9F8F6] rounded-lg border border-dashed border-[#EAE7E1]">
                Scansiona un DDT di questo fornitore per iniziare a imparare gli articoli.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                {items.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`supplier-item-${i}`}
                    className="flex items-center justify-between gap-2 p-3 rounded-lg border border-[#EAE7E1] bg-white"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {it.code && (
                          <span className="text-[10px] font-mono text-[#8B6518] bg-[#FDF6E3] border border-[#F2DEB0] px-1.5 py-0.5 rounded">
                            {it.code}
                          </span>
                        )}
                        <span className="text-sm font-medium truncate">{it.name}</span>
                      </div>
                      <div className="text-xs text-[#8E918C] mt-0.5 flex items-center gap-2 flex-wrap">
                        {it.category && <span>{it.category}</span>}
                        {it.weight && <span>· {it.weight}</span>}
                        {it.last_price != null && <span>· {formatCurrency(it.last_price)}</span>}
                      </div>
                    </div>
                    {it.seen_count > 1 && (
                      <span className="text-[10px] font-semibold text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-2 py-0.5 rounded-md flex items-center gap-1 flex-shrink-0">
                        <CheckCircle2 size={10} />
                        {it.seen_count}x
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {supplier.last_seen_at && (
            <div className="text-xs text-[#8E918C] text-center pt-4 border-t border-[#EAE7E1]">
              Ultimo documento: {formatDateTime(supplier.last_seen_at)}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
